import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Info, CheckCircle2, AlertCircle,
  CheckCheck, Bell, Clock, Eye, Archive, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AlertasScreen({ userId }) {
  const [alertasNaoLidos, setAlertasNaoLidos] = useState([]);
  const [alertasLidos, setAlertasLidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('novos');
  const prevCountRef = useRef(0);
  const firstLoadRef = useRef(true);
  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupAlerta, setPopupAlerta] = useState(null);
  const [popupQueue, setPopupQueue] = useState([]);
  // Detail modal state
  const [selectedAlerta, setSelectedAlerta] = useState(null);

  const fetchAlertas = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/configuracoes/alertas/activos`, {
        params: { usuario_id: userId, perfil: 'operador' }
      });
      const data = res.data || {};
      setAlertasNaoLidos(data.nao_lidos || []);
      setAlertasLidos(data.ultimos_lidos || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAlertas();
    const interval = setInterval(fetchAlertas, 15000);
    return () => clearInterval(interval);
  }, [fetchAlertas]);

  // Popup para alertas nao lidos e nao vistos na primeira carga + novos
  useEffect(() => {
    if (alertasNaoLidos.length === 0) return;
    
    // Alertas que ainda nao foram vistos (visto=false ou undefined)
    const naoVistos = alertasNaoLidos.filter(a => !a.visto);
    
    if (firstLoadRef.current && naoVistos.length > 0) {
      // Primeira carga: popup para todos os nao vistos
      firstLoadRef.current = false;
      prevCountRef.current = alertasNaoLidos.length;
      setPopupQueue(naoVistos);
      setPopupAlerta(naoVistos[0]);
      setShowPopup(true);
      return;
    }
    
    // Novos alertas durante a sessao
    if (alertasNaoLidos.length > prevCountRef.current && prevCountRef.current > 0) {
      const newest = alertasNaoLidos[0];
      if (newest && !newest.visto) {
        setPopupAlerta(newest);
        setShowPopup(true);
      }
    }
    prevCountRef.current = alertasNaoLidos.length;
  }, [alertasNaoLidos]);

  const handlePopupVisto = async () => {
    if (!popupAlerta) return;
    try {
      await axios.post(`${API}/alertas/${popupAlerta.id}/visto`, null, {
        params: { usuario_id: userId }
      });
    } catch { /* silent */ }
    
    // Atualizar estado local
    setAlertasNaoLidos(prev => prev.map(a => 
      a.id === popupAlerta.id ? { ...a, visto: true } : a
    ));
    
    // Proximo na queue
    const remaining = popupQueue.filter(a => a.id !== popupAlerta.id);
    setPopupQueue(remaining);
    if (remaining.length > 0) {
      setPopupAlerta(remaining[0]);
    } else {
      setShowPopup(false);
      setPopupAlerta(null);
    }
  };

  const marcarVisto = async (id) => {
    if (!userId) return;
    try {
      await axios.post(`${API}/alertas/${id}/visto`, null, {
        params: { usuario_id: userId }
      });
      setAlertasNaoLidos(prev => prev.map(a => 
        a.id === id ? { ...a, visto: true } : a
      ));
    } catch { /* silent */ }
  };

  const arquivar = async (id) => {
    if (!userId) return;
    try {
      await axios.post(`${API}/alertas/${id}/ler`, null, {
        params: { usuario_id: userId }
      });
      const alerta = alertasNaoLidos.find(a => a.id === id);
      if (alerta) {
        setAlertasNaoLidos(prev => prev.filter(a => a.id !== id));
        setAlertasLidos(prev => [{ ...alerta, visto: true }, ...prev]);
      }
    } catch { /* silent */ }
  };

  const apagar = async (id) => {
    if (!userId) return;
    try {
      await axios.delete(`${API}/alertas/${id}/utilizador/${userId}`);
      setAlertasLidos(prev => prev.filter(a => a.id !== id));
      toast.success('Alerta eliminado');
    } catch { /* silent */ }
  };

  const marcarTodasLidas = async () => {
    if (!userId) return;
    try {
      for (const alerta of alertasNaoLidos) {
        await axios.post(`${API}/alertas/${alerta.id}/ler`, null, {
          params: { usuario_id: userId }
        });
      }
      setAlertasLidos(prev => [...alertasNaoLidos.map(a => ({ ...a, visto: true })), ...prev]);
      setAlertasNaoLidos([]);
    } catch { /* silent */ }
  };

  const getIcon = (tipo, prioridade) => {
    if (prioridade === 'alta' || prioridade === 'urgente') {
      return { Icon: AlertTriangle, bg: '#FEF3C7', color: '#F59E0B', label: 'Urgente' };
    }
    switch (tipo) {
      case 'warning': case 'alerta':
        return { Icon: AlertTriangle, bg: '#FEF3C7', color: '#F59E0B', label: 'Aviso' };
      case 'info': case 'sistema': case 'gestor':
        return { Icon: Info, bg: '#E0F2FE', color: '#3B82F6', label: 'Info' };
      case 'success': case 'concluida':
        return { Icon: CheckCircle2, bg: '#DCFCE7', color: '#10B981', label: 'OK' };
      case 'error': case 'anomalia':
        return { Icon: AlertCircle, bg: '#FEE2E2', color: '#EF4444', label: 'Erro' };
      default:
        return { Icon: Bell, bg: '#E0F2FE', color: '#3B82F6', label: 'Alerta' };
    }
  };

  const formatTempo = (dataStr) => {
    if (!dataStr) return '';
    try {
      const data = new Date(dataStr);
      const agora = new Date();
      const diffMs = agora - data;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Agora mesmo';
      if (diffMin < 60) return `${diffMin} min`;
      const diffHoras = Math.floor(diffMin / 60);
      if (diffHoras < 24) return `${diffHoras}h`;
      const diffDias = Math.floor(diffHoras / 24);
      if (diffDias === 1) return 'Ontem';
      return `${diffDias} dias`;
    } catch { return ''; }
  };

  const naoLidas = alertasNaoLidos.length;
  const naoVistos = alertasNaoLidos.filter(a => !a.visto).length;
  const lista = viewMode === 'novos' ? alertasNaoLidos : alertasLidos;

  const renderCard = (alerta, isRecentes) => {
    const { Icon, bg, color, label } = getIcon(alerta.tipo, alerta.prioridade);
    const isVisto = !!alerta.visto;
    const isUnread = isRecentes && !isVisto;

    return (
      <div
        key={alerta.id}
        className="flex gap-3.5 rounded-2xl p-4 transition-all cursor-pointer active:scale-[0.99]"
        style={{
          background: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          borderLeft: isUnread ? '3px solid #EF4444' : isRecentes && isVisto ? '3px solid #3B82F6' : '3px solid transparent',
          opacity: isRecentes ? 1 : 0.8,
        }}
        data-testid={`alerta-${alerta.id}`}
        onClick={() => setSelectedAlerta(alerta)}
      >
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: bg, color }}>{label}</span>
            {isUnread && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 animate-pulse" style={{ background: '#EF4444' }} />}
            {isRecentes && isVisto && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: '#DBEAFE', color: '#2563EB' }}>Visto</span>
            )}
          </div>
          <span className="font-bold text-[15px]" style={{ color: '#1E293B' }}>
            {alerta.titulo || 'Alerta'}
          </span>
          <p className="text-sm leading-5 mt-0.5" style={{ color: '#64748B' }}>
            {alerta.mensagem || ''}
          </p>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs" style={{ color: '#94A3B8' }}>
              <Clock className="inline h-3 w-3 mr-0.5" style={{ verticalAlign: '-1px' }} />
              {formatTempo(alerta.data_notificacao || alerta.criado_em)}
            </p>
            {/* Botoes para Recentes */}
            {isRecentes && (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); arquivar(alerta.id); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:opacity-60"
                  style={{ background: '#64748B', color: '#FFFFFF' }}
                  data-testid={`btn-arquivar-${alerta.id}`}
                >
                  <Archive className="h-3.5 w-3.5" />
                  Arquivar
                </button>
                {!isVisto && (
                  <button
                    onClick={(e) => { e.stopPropagation(); marcarVisto(alerta.id); }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:opacity-60"
                    style={{ background: '#2563EB', color: '#FFFFFF' }}
                    data-testid={`btn-visto-${alerta.id}`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visto
                  </button>
                )}
              </div>
            )}
            {/* Botao Apagar para Historico */}
            {!isRecentes && (
              <button
                onClick={(e) => { e.stopPropagation(); apagar(alerta.id); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all active:opacity-60"
                style={{ background: '#FEE2E2', color: '#DC2626' }}
                data-testid={`btn-apagar-${alerta.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Apagar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Popup config
  const popupConfig = popupAlerta
    ? getIcon(popupAlerta.tipo, popupAlerta.prioridade)
    : { Icon: Bell, bg: '#E0F2FE', color: '#3B82F6', label: 'Alerta' };

  return (
    <div className="flex flex-col h-screen pb-24 overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* Header */}
      <div className="px-5 py-4" style={{ background: '#FFFFFF' }}>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-black" style={{ fontSize: '28px', color: '#1E293B' }} data-testid="alertas-title">
              Alertas
            </h1>
            {naoLidas > 0 && (
              <p className="text-sm mt-0.5" style={{ color: '#64748B' }}>
                {naoVistos > 0 ? `${naoVistos} novo${naoVistos > 1 ? 's' : ''}` : `${naoLidas} recente${naoLidas > 1 ? 's' : ''}`}
              </p>
            )}
          </div>
          {naoLidas > 0 && (
            <button
              onClick={marcarTodasLidas}
              className="flex items-center gap-1.5 py-2 px-3 rounded-xl text-sm font-semibold"
              style={{ color: '#0B3B30' }}
              data-testid="btn-marcar-todas-lidas"
            >
              <CheckCheck className="h-4 w-4" />
              Arquivar tudo
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => setViewMode('novos')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{
              background: viewMode === 'novos' ? '#0B3B30' : '#F1F5F9',
              color: viewMode === 'novos' ? '#FFFFFF' : '#64748B',
            }}
            data-testid="tab-novos"
          >
            <Bell className="inline h-4 w-4 mr-1.5" style={{ verticalAlign: '-2px' }} />
            Recentes {alertasNaoLidos.length > 0 && `(${alertasNaoLidos.length})`}
          </button>
          <button
            onClick={() => setViewMode('historico')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{
              background: viewMode === 'historico' ? '#0B3B30' : '#F1F5F9',
              color: viewMode === 'historico' ? '#FFFFFF' : '#64748B',
            }}
            data-testid="tab-historico"
          >
            <CheckCheck className="inline h-4 w-4 mr-1.5" style={{ verticalAlign: '-2px' }} />
            Historico {alertasLidos.length > 0 && `(${alertasLidos.length})`}
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent" style={{ borderColor: '#0B3B30', borderTopColor: 'transparent' }} />
          </div>
        ) : lista.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#F1F5F9' }}>
              {viewMode === 'novos'
                ? <Bell className="h-8 w-8" style={{ color: '#94A3B8' }} />
                : <CheckCircle2 className="h-8 w-8" style={{ color: '#94A3B8' }} />}
            </div>
            <p className="font-semibold text-sm" style={{ color: '#64748B' }}>
              {viewMode === 'novos' ? 'Sem alertas recentes' : 'Sem historico'}
            </p>
          </div>
        ) : (
          lista.map(a => renderCard(a, viewMode === 'novos'))
        )}
      </div>

      {/* Modal de Detalhe Expandido */}
      <Dialog open={!!selectedAlerta} onOpenChange={(open) => { if (!open) setSelectedAlerta(null); }}>
        <DialogContent className="max-w-md mx-auto rounded-2xl border-0 shadow-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>{selectedAlerta?.titulo || 'Detalhe do Alerta'}</DialogTitle>
          </DialogHeader>
          {selectedAlerta && (() => {
            const cfg = getIcon(selectedAlerta.tipo, selectedAlerta.prioridade);
            return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ background: cfg.bg, borderColor: `${cfg.color}33` }}>
                  <div className="p-3 rounded-full bg-white/80">
                    <cfg.Icon className="h-7 w-7" style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase" style={{ color: cfg.color }}>{cfg.label}</p>
                    <h3 className="font-bold text-lg" style={{ color: '#1E293B' }}>
                      {selectedAlerta.titulo}
                    </h3>
                  </div>
                </div>
                <div className="px-3 py-4 bg-stone-50 border border-stone-200 rounded-xl">
                  <p className="text-stone-800 leading-relaxed text-base">
                    {selectedAlerta.mensagem}
                  </p>
                </div>
                <p className="text-xs text-stone-400 px-2">
                  <Clock className="inline h-3 w-3 mr-1" />
                  {formatTempo(selectedAlerta.data_notificacao || selectedAlerta.criado_em)}
                </p>
                <div className="flex gap-2">
                  {!selectedAlerta.visto && (
                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-1" onClick={() => { marcarVisto(selectedAlerta.id); setSelectedAlerta(null); }}>
                      <Eye className="h-4 w-4" /> Marcar como Visto
                    </Button>
                  )}
                  <Button variant="outline" className="flex-1 rounded-full gap-1" onClick={() => { arquivar(selectedAlerta.id); setSelectedAlerta(null); }}>
                    <Archive className="h-4 w-4" /> Arquivar
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Popup para novo alerta — X fecha e permite navegar */}
      <Dialog open={showPopup} onOpenChange={(open) => { if (!open) { setShowPopup(false); setPopupQueue([]); setPopupAlerta(null); } }}>
        <DialogContent 
          className="max-w-sm mx-auto rounded-2xl border-0 shadow-2xl" 
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={() => { setShowPopup(false); setPopupQueue([]); setPopupAlerta(null); }}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{popupAlerta?.titulo || 'Novo Alerta'}</DialogTitle>
          </DialogHeader>
          {popupAlerta && (
            <div className="space-y-4">
              {/* Icon + Label */}
              <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ background: popupConfig.bg, borderColor: `${popupConfig.color}33` }}>
                <div className="p-2 rounded-full bg-white/80">
                  <popupConfig.Icon className="h-6 w-6" style={{ color: popupConfig.color }} />
                </div>
                <div>
                  <h3 className="font-bold text-base" style={{ color: '#1E293B' }}>
                    {popupAlerta.titulo}
                  </h3>
                  <p className="text-xs" style={{ color: popupConfig.color }}>
                    {popupConfig.label}
                    {popupQueue.length > 1 && ` (${popupQueue.indexOf(popupAlerta) + 1}/${popupQueue.length})`}
                  </p>
                </div>
              </div>

              {/* Mensagem */}
              <div className="px-2 py-3 bg-stone-50 border border-stone-200 rounded-xl">
                <p className="text-stone-800 leading-relaxed font-medium text-sm">
                  {popupAlerta.mensagem}
                </p>
              </div>

              {/* Data */}
              <p className="text-xs text-stone-400 px-2">
                <Clock className="inline h-3 w-3 mr-1" />
                {formatTempo(popupAlerta.data_notificacao || popupAlerta.criado_em)}
              </p>

              {/* Botao confirmar */}
              <Button
                className="w-full h-12 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-base"
                onClick={handlePopupVisto}
                data-testid="popup-visto-btn"
              >
                <Eye className="h-5 w-5 mr-2" />
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
